import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAuditRecord, deleteAuditRecord, listAuditRecords, listAuditUsers, updateAuditRecord } from '../../utils/auditApiClient';
import { Audit, AuditInput, AuditListFilter, AuditPagination, AuditUpdateInput } from '../../utils/auditTypes';

export const name = 'audits';

export interface AuditsState {
	items: Audit[];
	pagination: AuditPagination;
	users: string[];
	loading: boolean;
	error: string | null;
}

const initialState: AuditsState = {
	items: [],
	pagination: { page: 1, pageSize: 20, totalItems: 0, totalPages: 0 },
	users: [],
	loading: false,
	error: null
};

export const fetchAudits = createAsyncThunk(`${name}/fetchAudits`, (filter: AuditListFilter = {}) => listAuditRecords(filter));

export const createAudit = createAsyncThunk(`${name}/createAudit`, (audit: AuditInput) => createAuditRecord(audit));

export const updateAudit = createAsyncThunk(`${name}/updateAudit`, (audit: AuditUpdateInput) => updateAuditRecord(audit));

export const deleteAudit = createAsyncThunk(`${name}/deleteAudit`, (id: number) => deleteAuditRecord(id));

export const fetchAuditUsers = createAsyncThunk(`${name}/fetchAuditUsers`, () => listAuditUsers());

const errorMessage = (error: { message?: string } | undefined): string => error?.message ?? 'Unknown error';

export const auditsSlice = createSlice({
	name,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchAudits.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchAudits.fulfilled, (state, { payload }) => {
				state.loading = false;
				state.items = payload.items;
				state.pagination = payload.pagination;
			})
			.addCase(fetchAudits.rejected, (state, action) => {
				state.loading = false;
				state.error = errorMessage(action.error);
			})
			.addCase(createAudit.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createAudit.fulfilled, (state, { payload }: PayloadAction<Audit>) => {
				state.loading = false;
				state.items = [payload, ...state.items];
			})
			.addCase(createAudit.rejected, (state, action) => {
				state.loading = false;
				state.error = errorMessage(action.error);
			})
			.addCase(updateAudit.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateAudit.fulfilled, (state, { payload }: PayloadAction<Audit>) => {
				state.loading = false;
				state.items = state.items.map((item) => (item.id === payload.id ? payload : item));
			})
			.addCase(updateAudit.rejected, (state, action) => {
				state.loading = false;
				state.error = errorMessage(action.error);
			})
			.addCase(deleteAudit.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteAudit.fulfilled, (state, { payload }: PayloadAction<{ id: number }>) => {
				state.loading = false;
				state.items = state.items.filter((item) => item.id !== payload.id);
			})
			.addCase(deleteAudit.rejected, (state, action) => {
				state.loading = false;
				state.error = errorMessage(action.error);
			})
			.addCase(fetchAuditUsers.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchAuditUsers.fulfilled, (state, { payload }: PayloadAction<string[]>) => {
				state.loading = false;
				state.users = payload;
			})
			.addCase(fetchAuditUsers.rejected, (state, action) => {
				state.loading = false;
				state.error = errorMessage(action.error);
			});
	}
});

export default auditsSlice.reducer;
